import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../../Config';

const InfoRow = ({ label, value, full }) => (
  <Box sx={{ gridColumn: full ? '1 / -1' : 'auto' }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography fontWeight={500}>{value || '—'}</Typography>
  </Box>
);

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  const [hasDigiLock, setHasDigiLock] = useState(false);
  const [isFrontLock, setIsFrontLock] = useState(false);
  const [hasUpSale, setHasUpSale] = useState(false);

  const [locks, setLocks] = useState([]);
  const [selectedLockForRoom, setSelectedLockForRoom] = useState({});

  const [frontLockDialogOpen, setFrontLockDialogOpen] = useState(false);
  const [selectedFrontLock, setSelectedFrontLock] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const showSnack = (msg, severity) => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const fetchProperty = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${config.apiBaseUrl}/admin/getProperty/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        if (res.ok && data.propertyData) {
          const p = data.propertyData;
          setProperty(p);
          setHasDigiLock(p.hasDigiLock || false);
          setIsFrontLock(p.isFrontLock || false);
          setHasUpSale(p.hasUpSale || false);

          const preselected = {};
          p.rooms?.forEach((room) => {
            if (room.lock?.lockId) {
              preselected[room.roomID] = room.lock.lockId;
            }
          });
          setSelectedLockForRoom(preselected);
        } else {
          setProperty(null);
        }
      } catch (err) {
        console.error(err);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  useEffect(() => {
    const fetchLocks = async () => {
      if (!hasDigiLock) {
        setLocks([]);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${config.apiBaseUrl}/admin/getLinkLocksList`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setLocks(res.ok ? data.data || [] : []);
      } catch {
        setLocks([]);
      }
    };

    fetchLocks();
  }, [hasDigiLock]);

  const handleDigiLockToggle = async (e) => {
    const value = e.target.checked;
    setHasDigiLock(value);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/updateDigiLockStatus/${id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ hasDigiLock: value }),
      });
      if (!res.ok) throw new Error();
      showSnack('DigiLock updated', 'success');
    } catch {
      setHasDigiLock(!value);
      showSnack('Failed to update DigiLock', 'error');
    }
  };

  const handleUpSaleToggle = async (e) => {
    const value = e.target.checked;
    setHasUpSale(value);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${config.apiBaseUrl}/admin/property/upsale-status/${property._id}/${value}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error();
      showSnack('Upsell updated', 'success');
    } catch {
      setHasUpSale(!value);
      showSnack('Failed to update Upsell', 'error');
    }
  };

  const handleFrontLockToggle = async (e) => {
    const value = e.target.checked;

    if (value) {
      setFrontLockDialogOpen(true);
    } else {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${config.apiBaseUrl}/admin/updateFrontLockStatus/${id}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isFrontLock: false }),
        });
        if (!res.ok) throw new Error();
        setIsFrontLock(false);
        showSnack('Front lock disabled', 'success');
      } catch {
        showSnack('Failed to disable front lock', 'error');
      }
    }
  };

  const handleConfirmFrontLock = async () => {
    if (!selectedFrontLock) {
      showSnack('Please select a lock', 'warning');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/updateFrontLockStatus/${id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isFrontLock: true,
          lockId: selectedFrontLock,
        }),
      });
      if (!res.ok) throw new Error();
      setIsFrontLock(true);
      setFrontLockDialogOpen(false);
      setSelectedFrontLock('');
      showSnack('Front lock linked', 'success');
    } catch {
      showSnack('Failed to link front lock', 'error');
    }
  };

  const handleCancelFrontLock = () => {
    setFrontLockDialogOpen(false);
    setSelectedFrontLock('');
    setIsFrontLock(false);
  };

  const handleLockSelectChange = (roomID) => async (e) => {
    const lockId = e.target.value;
    setSelectedLockForRoom((prev) => ({ ...prev, [roomID]: lockId }));

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/linkLockToRoom`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyID: property.propertyID,
          roomID,
          lockId,
        }),
      });
      if (!res.ok) throw new Error();
      showSnack('Lock linked to room', 'success');
    } catch {
      showSnack('Failed to link lock', 'error');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!property) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Property not found</Typography>
      </Box>
    );
  }

  const {
    propertyName,
    propertyID,
    propertyPhone,
    propertyEmail,
    propertyAddress,
    propertyAmenities,
    rooms = [],
    propertyCurrency,
  } = property;

  const fullAddress = `${propertyAddress?.propertyAddress1 || ''}, ${propertyAddress?.propertyCity || ''}, ${propertyAddress?.propertyState || ''}, ${propertyAddress?.propertyZip || ''}, ${propertyAddress?.propertyCountry || ''}`;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={1} mb={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            {propertyName}
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={3}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Property Settings
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <FormControlLabel
              control={<Switch checked={hasDigiLock} onChange={handleDigiLockToggle} />}
              label="DigiLock"
            />
            {hasDigiLock && (
              <FormControlLabel
                control={<Switch checked={isFrontLock} onChange={handleFrontLockToggle} />}
                label="Front Lock"
              />
            )}
            <FormControlLabel
              control={<Switch checked={hasUpSale} onChange={handleUpSaleToggle} />}
              label="Upsell"
            />
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Property Information
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <InfoRow label="PropertyID" value={propertyID} full />
            <InfoRow label="Phone" value={propertyPhone} />
            <InfoRow label="Email" value={propertyEmail} />
            <InfoRow label="Currency" value={propertyCurrency?.currencyCode} />
            <InfoRow label="Address" value={fullAddress} full />
          </Box>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Amenities
          </Typography>
          {propertyAmenities?.length ? (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {propertyAmenities.map((a, i) => (
                <Chip key={i} label={a} variant="outlined" />
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">No amenities listed</Typography>
          )}
        </Paper>

        {rooms.length > 0 && (
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Room Management
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell>Room</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Guests</TableCell>
                  {hasDigiLock && <TableCell>Lock</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rooms.map((room) => (
                  <TableRow key={room.roomID} hover>
                    <TableCell>{room.roomName}</TableCell>
                    <TableCell>{room.roomTypeName}</TableCell>
                    <TableCell align="center">{room.maxGuests}</TableCell>
                    {hasDigiLock && (
                      <TableCell>
                        <Select
                          size="small"
                          fullWidth
                          value={selectedLockForRoom[room.roomID] || ''}
                          onChange={handleLockSelectChange(room.roomID)}
                        >
                          {locks.map((lock) => (
                            <MenuItem key={lock._id} value={lock.lockId}>
                              {lock.lockAlias || lock.lockName}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Stack>

      <Dialog
        open={frontLockDialogOpen}
        onClose={handleCancelFrontLock}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            width: { xs: '90%', sm: 400 },
            background: 'linear-gradient(180deg, #E0F7F4 0%, #F9FFFE 100%)',
            color: '#fff',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            textAlign: 'center',
            fontSize: '1.5rem',
            color: '#00bfa5',
            mb: 1,
          }}
        >
          Select Front Lock
        </DialogTitle>

        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            color: '#ccc',
            mb: 3,
            px: 3,
          }}
        >
          Choose which Lock should be configured as the main front lock for this property.
        </Typography>

        <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
          <FormControl
            fullWidth
            size="small"
            sx={{
              backgroundColor: '#fff',
              borderRadius: 1,
              width: '100%',
            }}
          >
            <InputLabel id="front-lock-select-label" sx={{ color: '#333' }}>
              Select Lock
            </InputLabel>
            <Select
              labelId="front-lock-select-label"
              value={selectedFrontLock}
              label="Select Lock"
              onChange={(e) => setSelectedFrontLock(e.target.value)}
              sx={{
                '& .MuiSelect-select': {
                  color: '#000',
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 250,
                    width: 260,
                  },
                },
              }}
            >
              {locks.length > 0 ? (
                locks.map((lock) => (
                  <MenuItem key={lock._id} value={lock.lockId}>
                    {lock.lockAlias || lock.lockName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No locks available</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions
          sx={{
            mt: 3,
            justifyContent: 'center',
            gap: 2,
            pb: 2,
          }}
        >
          <Button
            onClick={handleCancelFrontLock}
            variant="outlined"
            color="error"
            sx={{
              borderRadius: 2,
              px: 3,
              borderColor: '#ff5252',
              color: '#ff5252',
              '&:hover': {
                borderColor: '#ff1744',
                backgroundColor: 'rgba(255,82,82,0.1)',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmFrontLock}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              backgroundColor: '#00bfa5',
              '&:hover': {
                backgroundColor: '#00a48e',
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
}
